export interface TradeInfoRecord {
  value: number
  netMass: number
}

export interface CommodityUnitValue {
  commodity_code: string
  average_unit_value: number | null
  unit: string
  data_points: number
  error: string | null
}

async function fetchTradeInfoData(commodityCode: string): Promise<TradeInfoRecord[]> {
  try {
    const commodityId = parseInt(commodityCode.substring(0, 8))
    const url = `https://api.uktradeinfo.com/OTS?$filter=CommodityId%20eq%20${commodityId}&$select=Value,NetMass&$top=100`

    console.log('Fetching UK Trade Info data from:', url)

    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`API returned ${response.status}: ${response.statusText}`)
    }

    const data = await response.json() as any

    if (!Array.isArray(data)) {
      console.warn('Unexpected response format from UK Trade Info API:', data)
      return []
    }

    const records: TradeInfoRecord[] = data
      .filter((item: any) => item.Value && item.NetMass && item.NetMass > 0)
      .map((item: any) => ({
        value: parseFloat(String(item.Value)),
        netMass: parseFloat(String(item.NetMass))
      }))

    console.log(`Retrieved ${records.length} trade records for commodity ${commodityId}`)
    return records
  } catch (error) {
    console.error('Error fetching trade info data:', error)
    throw error
  }
}

export async function getCommodityUnitValue(commodityCode: string): Promise<CommodityUnitValue> {
  try {
    const records = await fetchTradeInfoData(commodityCode)

    if (records.length === 0) {
      return {
        commodity_code: commodityCode,
        average_unit_value: null,
        unit: 'GBP per kg',
        data_points: 0,
        error: 'No trade data found for this commodity code'
      }
    }

    const totalValue = records.reduce((sum, record) => sum + record.value, 0)
    const totalMass = records.reduce((sum, record) => sum + record.netMass, 0)

    if (totalMass === 0) {
      return {
        commodity_code: commodityCode,
        average_unit_value: null,
        unit: 'GBP per kg',
        data_points: records.length,
        error: 'Total net mass is zero - cannot calculate unit value'
      }
    }

    const averageUnitValue = totalValue / totalMass

    return {
      commodity_code: commodityCode,
      average_unit_value: parseFloat(averageUnitValue.toFixed(2)),
      unit: 'GBP per kg',
      data_points: records.length,
      error: null
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    return {
      commodity_code: commodityCode,
      average_unit_value: null,
      unit: 'GBP per kg',
      data_points: 0,
      error: `Failed to retrieve commodity value: ${errorMessage}`
    }
  }
}

export async function getCommodityUnitValueBatch(commodityCodes: string[]): Promise<CommodityUnitValue[]> {
  const unique = Array.from(new Set(commodityCodes.filter(c => c && c.length >= 8)))

  const results = await Promise.all(
    unique.map(code => getCommodityUnitValue(code))
  )

  return results
}
